import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { getStoredData } from "../../utils/localStorage";

const API_URL = process.env.REACT_APP_API_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const GOOGLE_MAPS_SCRIPT_ID = "google-maps-js-api";

let googleMapsPromise = null;

const MONTHS_PT = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const MONTHS_MAP = {
  janeiro: 0,
  fevereiro: 1,
  marco: 2,
  março: 2,
  abril: 3,
  maio: 4,
  junho: 5,
  julho: 6,
  agosto: 7,
  setembro: 8,
  outubro: 9,
  novembro: 10,
  dezembro: 11,
};

const TIPO_ENCOMENDA_MAP = {
  "Pequeno, estilo envelope até 1kg": {
    tipo_encomenda_nr: "1",
    quantidade_maxima_carga: "1",
  },
  "Caixa de Sapato/Sacola – 1 kg a 5 kg": {
    tipo_encomenda_nr: "2",
    quantidade_maxima_carga: "5",
  },
  "Mochila/Mala – 5 kg a 10 kg": {
    tipo_encomenda_nr: "3",
    quantidade_maxima_carga: "10",
  },
  "Mala/Caixa – 10 kg até 23 kg": {
    tipo_encomenda_nr: "4",
    quantidade_maxima_carga: "23",
  },
};

function parseDateFlexible(value) {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value;
  }

  const str = String(value).trim();

  // DD/MM/YYYY
  let match = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (match) {
    const day = Number(match[1]);
    const month = Number(match[2]) - 1;
    const year = Number(match[3]);
    return new Date(Date.UTC(year, month, day));
  }

  // YYYY-MM-DD
  match = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    return new Date(Date.UTC(year, month, day));
  }

  // "23 Novembro, 2026"
  match = str.match(/^(\d{1,2})\s+([A-Za-zÀ-ÿ]+)\s*,\s*(\d{4})$/);
  if (match) {
    const day = Number(match[1]);
    const monthName = match[2]
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    const month = MONTHS_MAP[monthName];
    const year = Number(match[3]);

    if (month === undefined) return null;

    return new Date(Date.UTC(year, month, day));
  }

  return null;
}

function formatDateLongBR(value) {
  const date = parseDateFlexible(value);
  if (!date) return "";

  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = MONTHS_PT[date.getUTCMonth()];
  const year = date.getUTCFullYear();

  return `${day} ${month}, ${year}`;
}

function formatDateShortBR(value) {
  const date = parseDateFlexible(value);
  if (!date) return "";

  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();

  return `${day}/${month}/${year}`;
}

function normalizeDateFields(displayValue, searchValue) {
  const parsed =
    parseDateFlexible(searchValue) || parseDateFlexible(displayValue);

  return {
    data: formatDateLongBR(parsed) || displayValue || "",
    data_para_busca: formatDateShortBR(parsed) || searchValue || "",
  };
}

function loadGoogleMaps() {
  if (window.google?.maps) {
    return Promise.resolve(window.google.maps);
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return Promise.reject(
      new Error("REACT_APP_GOOGLE_API_KEY não está configurada."),
    );
  }

  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);

    if (existingScript) {
      existingScript.addEventListener("load", () =>
        resolve(window.google.maps),
      );
      existingScript.addEventListener("error", () => {
        reject(new Error("Falha ao carregar Google Maps API."));
      });
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;

    script.onload = () => resolve(window.google.maps);
    script.onerror = () =>
      reject(new Error("Falha ao carregar Google Maps API."));

    document.head.appendChild(script);
  });

  return googleMapsPromise;
}

function calcularDuracaoViagem(diaInicial, diaFinal) {
  const inicio = parseDateFlexible(diaInicial);
  const fim = parseDateFlexible(diaFinal);

  if (!inicio || !fim) return "";

  const diffMs = fim.getTime() - inicio.getTime();
  const diffDias = Math.max(0, Math.round(diffMs / (1000 * 60 * 60 * 24)));

  return diffDias === 1 ? "1 dia" : `${diffDias} dias`;
}

function getTipoEncomendaMeta(tipoEncomenda) {
  return (
    TIPO_ENCOMENDA_MAP[tipoEncomenda] || {
      tipo_encomenda_nr: "",
      quantidade_maxima_carga: "",
    }
  );
}

function parseTrajetoItem(
  item,
  origemDisplayDate,
  origemSearchDate,
  origemHora,
  destinoDisplayDate,
  destinoSearchDate,
  destinoHora,
) {
  if (!item || typeof item !== "string") return null;

  const [cidadeParte, metaParte] = item.split("#");
  const cidade = (cidadeParte || "").trim();

  if (!cidade) return null;

  // 🚨 CASO SEM DATA/HORA (#)
  if (!metaParte) {
    return {
      cidade,

      // partida = origem
      data_partida: formatDateLongBR(origemDisplayDate),
      data_partida_para_busca: formatDateShortBR(origemSearchDate),
      horas_partida: origemHora,

      // chegada = destino
      data_chegada: formatDateLongBR(destinoDisplayDate),
      data_chegada_para_busca: formatDateShortBR(destinoSearchDate),
      horas_chegada: destinoHora,
    };
  }

  // ✅ CASO COM DATA/HORA (#)
  const [dataParte, horaParte] = metaParte.split("$");

  const normalized = normalizeDateFields(dataParte, dataParte);

  return {
    cidade,

    // nesse caso ele representa um ponto específico (usa mesma data nos dois lados)
    data_partida: normalized.data,
    data_partida_para_busca: normalized.data_para_busca,
    horas_partida: horaParte || origemHora,

    data_chegada: normalized.data,
    data_chegada_para_busca: normalized.data_para_busca,
    horas_chegada: horaParte || destinoHora,
  };
}

function normalizeTrajetos(cidadesTrajeto, origem, destino) {
  if (!Array.isArray(cidadesTrajeto)) return [];

  return cidadesTrajeto
    .map((item) =>
      parseTrajetoItem(
        item,
        origem.data,
        origem.data_para_busca,
        origem.horas,
        destino.data,
        destino.data_para_busca,
        destino.horas,
      ),
    )
    .filter(Boolean);
}

function buildNodes(data) {
  const paisBusca = data.pais_origem || data.pais_destino || "Brasil";

  const origemDates = normalizeDateFields(
    data.dia_mes_ano_ida_viagem,
    data.dia_mes_ano_ida_viagem_para_busca,
  );

  const destinoDates = normalizeDateFields(
    data.dia_mes_ano_ida_destino,
    data.dia_mes_ano_ida_destino_para_busca,
  );

  const origem = {
    cidade: data.cidade_origem_main,
    local_cidade: data.cidade_origem,
    data: origemDates.data,
    data_para_busca: origemDates.data_para_busca,
    horas: data.horas_ida_viagem,
    pais: paisBusca,
    tipo: "origem",
  };

  const destino = {
    cidade: data.cidade_destino_main,
    local_cidade: data.cidade_destino,
    data: destinoDates.data,
    data_para_busca: destinoDates.data_para_busca,
    horas: data.horas_ida_destino,
    pais: paisBusca,
    tipo: "destino",
  };

  const trajetos = normalizeTrajetos(data.cidades_trajeto, origem, destino).map(
    (item) => ({
      ...item,
      pais: paisBusca,
      tipo: "trajeto",
    }),
  );

  return [origem, ...trajetos, destino];
}

function geocodeCidade(maps, cidade, pais = "Brasil") {
  return new Promise((resolve, reject) => {
    const geocoder = new maps.Geocoder();

    geocoder.geocode(
      {
        address: `${cidade}, ${pais}`,
      },
      (results, status) => {
        if (status === "OK" && results?.[0]) {
          const location = results[0].geometry.location;

          resolve({
            lat: location.lat(),
            lng: location.lng(),
            formatted_address: results[0].formatted_address,
          });
          return;
        }

        reject(
          new Error(`Não foi possível geocodificar "${cidade}" (${status}).`),
        );
      },
    );
  });
}

function calcularDistanciaKm(maps, origem, destino) {
  return new Promise((resolve, reject) => {
    const service = new maps.DistanceMatrixService();

    service.getDistanceMatrix(
      {
        origins: [new maps.LatLng(origem.lat, origem.lng)],
        destinations: [new maps.LatLng(destino.lat, destino.lng)],
        travelMode: maps.TravelMode.DRIVING,
        unitSystem: maps.UnitSystem.METRIC,
      },
      (response, status) => {
        if (status !== "OK") {
          reject(new Error(`Erro ao calcular distância (${status}).`));
          return;
        }

        const element = response?.rows?.[0]?.elements?.[0];
        if (!element || element.status !== "OK") {
          reject(new Error("Não foi possível calcular a distância."));
          return;
        }

        const distanceMeters = element.distance.value;
        const km = Math.round(distanceMeters / 1000);

        resolve(`${km} KM`);
      },
    );
  });
}

async function montarViagens(data) {
  const maps = await loadGoogleMaps();
  const nodes = buildNodes(data);

  const paisBusca = data.pais_origem || data.pais_destino || "Brasil";

  const cidadesUnicas = [...new Set(nodes.map((node) => node.cidade))];
  const coordenadasPorCidade = {};

  for (const cidade of cidadesUnicas) {
    coordenadasPorCidade[cidade] = await geocodeCidade(maps, cidade, paisBusca);
  }

  const nodesComCoordenadas = nodes.map((node) => ({
    ...node,
    lat: coordenadasPorCidade[node.cidade].lat,
    lng: coordenadasPorCidade[node.cidade].lng,
    formatted_address: coordenadasPorCidade[node.cidade].formatted_address,
  }));

  const metaTipoEncomenda = getTipoEncomendaMeta(data.select_espaco_mala || "");

  const viagens = [];

  for (let i = 0; i < nodesComCoordenadas.length - 1; i++) {
    for (let j = i + 1; j < nodesComCoordenadas.length; j++) {
      const de = nodesComCoordenadas[i];
      const para = nodesComCoordenadas[j];
      const distanciaViagem = await calcularDistanciaKm(maps, de, para);

      const dataPartidaBusca =
        de.tipo === "trajeto" ? de.data_partida_para_busca : de.data_para_busca;

      const dataChegadaBusca =
        para.tipo === "trajeto"
          ? para.data_chegada_para_busca
          : para.data_para_busca;

      const duracaoViagem = calcularDuracaoViagem(
        dataPartidaBusca,
        dataChegadaBusca,
      );

      //console.log(para);
      //return;

      viagens.push({
        ...(data?.editar_trajeto === "sim" && { id: data.id }),
        pais_origem: data.pais_origem || "Brasil",
        cidade_de_onde_vai_sair: de.cidade,
        de: de?.local_cidade ?? de?.cidade ?? "",
        rota_selecionada: data.rota_selecionada || null,

        lat_cidade_origem: de.lat ?? null,
        lng_cidade_origem: de.lng ?? null,

        local_que_vai_receber_encomenda:
          data.local_que_vai_receber_encomenda || null,

        pais_destino: data.pais_destino || "Brasil",
        cidade_para_onde_vai: para.cidade,
        para: para?.local_cidade ?? para?.cidade ?? "",

        lat_cidade_destino: para.lat ?? null,
        lng_cidade_destino: para.lng ?? null,

        local_que_vai_deixar_encomenda:
          data.local_que_vai_deixar_encomenda || null,

        tipo_encomenda: data.select_espaco_mala || null,
        tipo_encomenda_nr: Number(metaTipoEncomenda.tipo_encomenda_nr) || null,
        quantidade_maxima_carga:
          Number(metaTipoEncomenda.quantidade_maxima_carga) || null,

        meio_transporte: data.qual_meio_transporte || null,

        data_partida: de.tipo === "trajeto" ? de.data_partida : de.data,
        data_partida_para_busca:
          de.tipo === "trajeto"
            ? de.data_partida_para_busca
            : de.data_para_busca,
        data_chegada: para.tipo === "trajeto" ? para.data_chegada : para.data,
        data_chegada_para_busca:
          para.tipo === "trajeto"
            ? para.data_chegada_para_busca
            : para.data_para_busca,

        horas_partida: de.tipo === "trajeto" ? de.horas_partida : de.horas,
        horas_chegada:
          para.tipo === "trajeto" ? para.horas_chegada : para.horas,

        informacao_sobre_trajeto: data.informacao_a_viagem || null,

        distancia_viagem: distanciaViagem,
        duracao_viagem: duracaoViagem,

        viagem_disponibilizada_cancelada: false,
        status: true,
        viagem_principal: i === 0 && j === nodesComCoordenadas.length - 1,
      });
    }
  }

  return viagens;
}

export function UseFinalizarCadastramentoMala() {
  const { authFetch, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const finalizarCadastramento = async () => {
    try {
      setLoading(true);
      setError("");

      const data = getStoredData();
      if (!data) {
        throw new Error("Nenhum dado encontrado no localStorage.");
      }

      const viagens = await montarViagens(data);

      let response;

      if (data?.editar_trajeto === "sim") {
        response = await authFetch(`${API_URL}/api/viagens/atualizar/`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            //Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ viagens }),
        });
      } else {
        response = await authFetch(`${API_URL}/api/viagens/finalizar/`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            //Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ viagens }),
        });
      }

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result?.detail || "Erro ao salvar viagens.");
      }

      return result;
    } catch (err) {
      setError(err.message || "Erro inesperado.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    finalizarCadastramento,
    loading,
    setLoading,
  };
}
